/**
 * Masking — one layer clipped by the shape (or the alpha) of another.
 *
 * Figma's model, kept deliberately: the LOWEST selected layer becomes the mask,
 * every layer above it in that selection is clipped by it, and the mask itself
 * stops painting — only its silhouette survives. That is what "Use as mask" does
 * everywhere else, so it is what it does here.
 *
 * Stored as two plain fields, so nothing about the document shape changes:
 *   - `element.isMask`  — this element is a mask (it paints nothing itself)
 *   - `element.maskId`  — this element is clipped by the element with that id
 *
 * Any layer can be the mask, and how it cuts depends on what it is made of —
 * `maskKind` below is the single answer both surfaces (the DOM canvas and the
 * Canvas2D export) ask for:
 *   - a path shape (star, hexagon, arrow…) → clip-path: path() / traceShapePath
 *   - a rectangle or an ellipse (or any plain box) → clip-path: inset()/ellipse()
 *   - a picture, a line of type, a QR, a barcode → its own ALPHA: an SVG <mask>
 *     on the canvas, a destination-in composite on the export. That is how a
 *     cut-out PNG, a word or a scanner code becomes a stencil.
 *
 * A table is the one type that cannot be rasterised cheaply on both surfaces, so
 * it masks by its box.
 *
 * Rotation caveat, stated rather than half-solved: a rotated MASKED element takes
 * its mask around with it (the mask is expressed in the element's own box). A
 * rotated MASK is treated as its unrotated box. Figma solves that with a mask
 * group; that is a bigger change than this is.
 */
import { ELEMENT_TYPES } from '../schema/documentSchema';
import { isPathShape, shapePathD } from '../schema/shapeLibrary';
import {
    getCornerRadii, getEffects, shadowColorCss, parseRgba, buildTextStroke,
} from '../panels/propertyControls';

export const isMaskElement = (el) => !!el?.isMask;
export const isMasked = (el) => !!el?.maskId;

/** The element that masks this one, if it is still in the document. */
export function maskFor(el, elements) {
    if (!el?.maskId) return null;
    const list = Array.isArray(elements) ? elements : [...(elements?.values?.() || [])];
    const mask = list.find((e) => e.id === el.maskId);
    return mask && mask.id !== el.id ? mask : null;
}

/** Does this element mask anything? (an orphan mask flag is just a hidden layer) */
export const masksAnything = (el, elements) => (
    isMaskElement(el) && (elements || []).some((e) => e.maskId === el.id)
);

const isImageMask = (mask) => mask?.type === ELEMENT_TYPES.IMAGE && !!mask.src;

const isEllipseMask = (mask) => (
    mask?.type === ELEMENT_TYPES.SHAPE && mask.shape === 'ellipse'
);

const isPathMask = (mask) => (
    mask?.type === ELEMENT_TYPES.SHAPE && isPathShape(mask.shape)
);

/**
 * How a layer cuts, in one place — the canvas and the export both ask here, so
 * neither can drift from the other.
 *
 *   path / ellipse / box  — geometry, clipped as vectors: crisp at any zoom
 *   image / text / qr / barcode — the layer's own ALPHA, which is Figma's
 *     default mask and the only honest answer for a shape made of glyphs or of
 *     scanner modules
 *
 * A table masks by its box: its alpha is a filled rectangle anyway, and it is
 * the one type neither surface can rasterise cheaply.
 */
export function maskKind(mask) {
    if (!mask) return null;
    if (isPathMask(mask)) return 'path';
    if (isEllipseMask(mask)) return 'ellipse';
    if (isImageMask(mask)) return 'image';
    if (mask.type === ELEMENT_TYPES.TEXT || mask.type === ELEMENT_TYPES.PLACEHOLDER) return 'text';
    if (mask.type === ELEMENT_TYPES.QRCODE) return 'qr';
    if (mask.type === ELEMENT_TYPES.BARCODE) return 'barcode';
    return 'box';
}

/**
 * Masks whose silhouette is DRAWN rather than clipped — an SVG <mask> on the
 * canvas.
 *
 * Pictures are on this list for a reason that has nothing to do with shape: CSS
 * `mask-image` is CORS-gated, and a saved template's images live in R2 on another
 * origin (see utils/templateMedia), which the bucket serves without the header
 * that would let CSS read them. The mask silently resolves to nothing and the
 * masked layer disappears — a white artboard where the artwork was. An SVG
 * <image> inside a <mask> paints the same cross-origin picture happily, so every
 * alpha mask goes that way instead. Probed both, on a cross-origin PNG.
 */
export const SVG_MASK_KINDS = new Set(['image', 'text', 'qr', 'barcode']);

/**
 * The mask layer's own Layer blur, in px.
 *
 * Figma masks by what the mask layer RENDERS, not by its geometry — so a blurred
 * mask has a blurred (soft, feathered) edge, and the layers it cuts fade out
 * through it. Every mask kind is affected, because layer blur is a style any
 * layer can carry: a blurred ellipse feathers, blurred type feathers, a blurred
 * cut-out PNG feathers.
 */
export const maskLayerBlur = (mask) => Math.max(0, Number(mask?.styles?.layerBlur) || 0);

/**
 * How far a mask's OWN fill reaches — its alpha, not its colour.
 *
 * A half-transparent mask reveals half, which is Figma's rule and the whole point
 * of an alpha mask. Only the geometry kinds are asked: a picture, a glyph or a QR
 * carries its alpha in its own pixels.
 *
 * A fill of NOTHING (transparent, or a gradient this cannot read a single alpha
 * out of) reads as opaque instead of as zero. Zero would make the subject vanish
 * with no way for the user to tell why — the same call the empty-mask fallback
 * already makes — and an unfilled rectangle dragged over a photo is far more
 * likely to mean "cut here" than "hide everything".
 */
function maskFillAlpha(mask, kind) {
    if (kind !== 'path' && kind !== 'ellipse' && kind !== 'box') return 1;
    const styles = mask.styles || {};
    // A SHAPE keeps its paint in `fill` / `fillType`; everything else (a text
    // block, a table) fills its box with `backgroundColor` (see
    // resolveFillBackground, which reads exactly this pair of key sets).
    const isShape = mask.type === ELEMENT_TYPES.SHAPE;
    const type = isShape ? (styles.fillType || styles.backgroundType) : styles.backgroundType;
    if (type && type !== 'solid') return 1;
    const a = parseRgba(isShape ? styles.fill : styles.backgroundColor)?.a;
    return a == null || a <= 0 ? 1 : a;
}

/** How far the mask's own OUTLINE pushes its silhouette outward. */
function maskStrokeGrow(mask, kind) {
    const styles = mask.styles || {};
    // Glyph outline — the one stroke that can sit outside the letterforms.
    if (kind === 'text') return buildTextStroke(styles)?.outset || 0;
    // A path shape strokes CENTRED, so half the weight lands outside the fill
    // (see elementRenderBounds). A box or an ellipse borders inward, like CSS.
    if (kind === 'path') return (Number(styles.strokeWidth) || 0) / 2;
    return 0;
}

/**
 * Everything about a mask layer that changes its ALPHA — the whole of what
 * masking has to respect, resolved once so the DOM canvas and the export cannot
 * drift.
 *
 * In: layer opacity, fill alpha, an outline that reaches past the fill, every
 * drop shadow (and a text layer's text shadows), and Layer blur. That is the
 * complete list of effects that alter what a layer covers.
 *
 * Out, deliberately: inner shadow, background blur, noise, texture, shader and
 * glass. Every one of them paints INSIDE the silhouette the layer already has
 * (see paintOverlays, which clips to the element's shape), so masking by them
 * would cut nothing differently. They are not ignored so much as beside the
 * point.
 *
 * Shadows come back BACK-TO-FRONT (the export's paint order) with their opacity
 * already folded into `alpha`, and blur radii stay in CSS terms: a shadow's
 * `blur` is a CSS blur RADIUS (σ = radius / 2), `blur` on the layer is a CSS
 * `filter: blur()` (σ = radius). Both surfaces convert the same way.
 */
export function maskAlphaEffects(mask) {
    const kind = maskKind(mask);
    const styles = mask?.styles || {};
    const opacity = Math.max(0, Math.min(1, Number(mask?.opacity ?? 1))) * maskFillAlpha(mask, kind);
    const blur = maskLayerBlur(mask);
    const grow = maskStrokeGrow(mask, kind);
    const raw = [
        ...getEffects(styles, 'dropShadow').map((fx) => ({ fx, type: 'dropShadow' })),
        // A text layer's shadow lives under its own key and is the only shadow a
        // mask made of words can cast.
        ...(kind === 'text' ? getEffects(styles, 'textShadow').map((fx) => ({ fx, type: 'textShadow' })) : []),
    ];
    const shadows = raw.map(({ fx, type }) => ({
        dx: Number(fx.offsetX) || 0,
        dy: Number(fx.offsetY) || 0,
        blur: Math.max(0, Number(fx.blur) || 0),
        spread: type === 'textShadow' ? 0 : (Number(fx.spread) || 0),
        alpha: parseRgba(shadowColorCss(fx, type))?.a ?? 1,
    })).filter((s) => s.alpha > 0).reverse();

    // How far past the mask's own box the silhouette can now reach — the padding
    // both surfaces need, or the soft edge gets cut back into a hard one.
    const shadowBleed = shadows.reduce((m, s) => Math.max(
        m,
        Math.max(Math.abs(s.dx), Math.abs(s.dy)) + s.blur + Math.max(0, s.spread),
    ), 0);

    return {
        opacity,
        blur,
        grow,
        shadows,
        bleed: Math.max(grow, shadowBleed + grow) + blur * 3,
        // Nothing here that a plain clip cannot already express?
        any: blur > 0 || opacity < 1 || grow > 0 || shadows.length > 0,
    };
}

/**
 * Does this mask need a DRAWN silhouette (an SVG <mask> on the canvas, a stencil
 * composite on the export) rather than a clip?
 *
 * Two reasons: the kind has no CSS shape at all (glyphs, QR modules, bars), or
 * the mask carries an effect that changes its alpha — a blur, a shadow, an
 * outline, less than full opacity. A clip is all-or-nothing at one hard edge and
 * can express none of those, so the silhouette has to be drawn and filtered.
 */
export const usesSvgMask = (mask) => (
    SVG_MASK_KINDS.has(maskKind(mask)) || maskAlphaEffects(mask).any
);

/**
 * The id of the <mask> built for one PAIR of layers. Per pair, not per mask: the
 * definition is written in the masked element's own coordinates, and two subjects
 * of the same mask sit at different offsets from it.
 */
export const maskDefId = (maskId, subjectId) => `emask-${maskId}-${subjectId}`;

/**
 * CSS that clips one element to its mask — the mask's box expressed in the
 * masked element's own coordinates, which is the space `clip-path` works in.
 */
export function maskCssFor(el, mask) {
    if (!el || !mask) return null;
    const dx = (Number(mask.x) || 0) - (Number(el.x) || 0);
    const dy = (Number(mask.y) || 0) - (Number(el.y) || 0);
    const mw = Math.max(1, Number(mask.width) || 1);
    const mh = Math.max(1, Number(mask.height) || 1);
    const kind = maskKind(mask);

    // Glyphs, QR modules and barcode bars are drawn into an SVG <mask> that the
    // canvas puts in the document beside the layer (see canvas/MaskDefs) — CSS
    // has no shape for any of them.
    // ...and so is any BLURRED mask, whatever its kind: `clip-path` cuts one hard
    // edge, and a layer blur is a soft one.
    if (usesSvgMask(mask)) {
        const url = `url(#${maskDefId(mask.id, el.id)})`;
        return { WebkitMaskImage: url, maskImage: url };
    }

    if (kind === 'path') {
        const d = shapePathD(mask, mw, mh, dx, dy);
        return d ? { clipPath: `path('${d}')` } : null;
    }

    if (kind === 'ellipse') {
        return { clipPath: `ellipse(${mw / 2}px ${mh / 2}px at ${dx + mw / 2}px ${dy + mh / 2}px)` };
    }

    // Anything else is masked by its BOX — a rectangle, with whatever corner
    // radius it carries (an image, a text block or a plain rect all land here).
    const w = Math.max(1, Number(el.width) || 1);
    const h = Math.max(1, Number(el.height) || 1);
    const { tl, tr, br, bl } = getCornerRadii(mask.styles || {});
    const round = (tl || tr || br || bl) ? ` round ${tl}px ${tr}px ${br}px ${bl}px` : '';
    return {
        clipPath: `inset(${dy}px ${w - (dx + mw)}px ${h - (dy + mh)}px ${dx}px${round})`,
    };
}

/**
 * Mask geometry in the masked element's PAINT space for the export: the origin
 * is the element's centre and the axes are the scene's (the caller un-rotates
 * first), so this is the same box the CSS above describes.
 */
export function maskBoxFor(el, mask) {
    const kind = maskKind(mask);
    return {
        x: (Number(mask.x) || 0) - ((Number(el.x) || 0) + (Number(el.width) || 0) / 2),
        y: (Number(mask.y) || 0) - ((Number(el.y) || 0) + (Number(el.height) || 0) / 2),
        w: Math.max(1, Number(mask.width) || 1),
        h: Math.max(1, Number(mask.height) || 1),
        kind,
        // Everything about the mask that changes its alpha — opacity, fill alpha,
        // outline, shadows, Layer blur. The stencil is put through these before it
        // knocks the element out (see the export's buildMaskStencil).
        fx: maskAlphaEffects(mask),
        // An alpha mask cannot be a clip: the element has to be composited into a
        // layer and knocked out with the mask's own pixels. A blurred mask of ANY
        // kind lands here too — see usesSvgMask.
        alpha: usesSvgMask(mask),
        ellipse: kind === 'ellipse',
        path: kind === 'path',
    };
}
